import { OverflowMenuVertical } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';

import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { Button } from 'bones/design-system/components/Button';
import { IconComponentType } from 'bones/design-system/components/icons/Icon';
import { Menu } from 'bones/design-system/components/select/Menu';
import {
  MenuItem,
  MenuItemProps,
} from 'bones/design-system/components/select/MenuItem';
import { MenuItems } from 'bones/design-system/components/select/MenuItems';
import { Tooltip } from 'bones/design-system/components/Tooltip';

export type VoiceboxChatActionItem = {
  badgeProps?: MenuItemProps['badgeProps'];
  disabled?: boolean;
  icon?: IconComponentType;
  onClick?: () => void;
  tooltip?: string;
} & ({ label: string; testId?: string } | { label?: string; testId: string });

export interface VoiceboxChatActionsProps {
  'data-testid'?: never;
  leftActions?: VoiceboxChatActionItem[];
  menuActions?: VoiceboxChatActionItem[];
  messagesRef: React.RefObject<HTMLDivElement>;
  rightActions?: VoiceboxChatActionItem[];
  testId?: string;
}

export const VoiceboxChatActions: React.VFC<VoiceboxChatActionsProps> = ({
  leftActions,
  menuActions,
  messagesRef,
  rightActions,
  testId,
}) => {
  const { testIds } = useVoiceboxSchema();

  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="vb-voicebox-chat-actions" data-testid={testId}>
      <div className="vb-voicebox-chat-actions-left">
        {leftActions?.map((item) => (
          <Tooltip
            key={item.label || /* istanbul ignore next */ item.testId}
            description={item.tooltip}
            disabled={isOpen || !item.tooltip}
          >
            <Button
              className={classNames('vb-voicebox-chat-action-button', {
                'vb-voicebox-chat-action-button--static': !item.onClick,
              })}
              disabled={item.disabled}
              leftIcon={item.icon}
              onClick={item.onClick}
              size="xs"
              testId={item.testId}
              variant="ghost"
            >
              {item.label}
            </Button>
          </Tooltip>
        ))}
      </div>
      <div className="vb-voicebox-chat-actions-right">
        {rightActions?.map((item) => (
          <Tooltip
            key={item.label || /* istanbul ignore next */ item.testId}
            description={item.tooltip}
            disabled={isOpen || !item.tooltip}
          >
            <Button
              className={classNames('vb-voicebox-chat-action-button', {
                'vb-voicebox-chat-action-button--static': !item.onClick,
              })}
              disabled={item.disabled}
              leftIcon={item.icon}
              onClick={item.onClick}
              size="xs"
              testId={item.testId}
              variant="ghost"
            >
              {item.label}
            </Button>
          </Tooltip>
        ))}
        {rightActions?.length && menuActions?.length ? (
          <div className="vb-voicebox-chat-actions-divider" />
        ) : null}
        {menuActions?.length ? (
          <Menu
            boundary={
              messagesRef.current || /* istanbul ignore next */ undefined
            }
            isOpen={isOpen}
            leftIcon={OverflowMenuVertical}
            menu={
              <MenuItems>
                {menuActions.map((item) => (
                  <MenuItem
                    key={item.label || /* istanbul ignore next */ item.testId}
                    badgeProps={item.badgeProps}
                    disabled={item.disabled}
                    leftIcon={item.icon}
                    onClick={item.onClick}
                    testId={item.testId}
                    text={item.label}
                  />
                ))}
              </MenuItems>
            }
            onInteraction={(nextIsOpen) => {
              setIsOpen(nextIsOpen);
            }}
            size="xs"
            testId={testIds.messages.menu.button}
          />
        ) : null}
      </div>
    </div>
  );
};
